import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import DataTable, { type DataTableColumn, type RowKey } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import ManufacturerLogo from '../../components/ManufacturerLogo';
import { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  createCmdbCard, deleteCmdbCard, errorMessage, fetchCmdbCard, fetchCmdbCards,
  fetchCmdbOptions, updateCmdbCard,
} from '../../lib/api';
import { countLabel } from '../../lib/format';
import { readOnlyNotice } from '../../lib/permissions';
import type { CmdbAttribute, CmdbCardRow, CmdbClassMeta, CmdbMetaResponse, CmdbOption } from '../../lib/types';
import CardFormModal from './CardFormModal';
import DeleteCardModal from './DeleteCardModal';
import StammIcon from './StammIcon';
import { referenceTargets } from './cardHelpers';
import {
  componentKindLabel, componentVersionLabel, filterComponentGroups, groupComponents,
  latestComponentVersion, newVersionValues, type ComponentGroup, type ComponentGroupFilter,
} from './componentCatalogModel';

type Dialog =
  | { kind: 'closed' }
  | { kind: 'form'; meta: CmdbClassMeta; row: CmdbCardRow | null; options: Record<string, CmdbOption[]>; initialValues?: Record<string, unknown>; notice?: string }
  | { kind: 'delete'; meta: CmdbClassMeta; row: CmdbCardRow };

interface Props {
  meta: CmdbMetaResponse;
  canWrite: boolean;
  onCountsChange: () => Promise<void>;
  renderValue: (attr: CmdbAttribute, row: CmdbCardRow) => ReactNode;
}

/** Components own their versions in the UI; CRUD and shared links keep their original identities. */
export default function ComponentCatalog({ meta, canWrite, onCountsChange, renderValue }: Props): ReactNode {
  const componentMeta = meta.classes.find((entry) => entry.name === 'KomponenteType')!;
  const versionMeta = meta.classes.find((entry) => entry.name === 'KomponenteVersion')!;
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ components: CmdbCardRow[]; versions: CmdbCardRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ComponentGroupFilter>('all');
  const [expanded, setExpanded] = useState<ReadonlySet<RowKey>>(new Set());
  const [dialog, setDialog] = useState<Dialog>({ kind: 'closed' });
  const [dialogBusy, setDialogBusy] = useState(false);
  const loadSeq = useRef(0);
  const dialogSeq = useRef(0);
  const focusedRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLElement>(null);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      // Search spans both complete lists, so a version match never loses its parent.
      const [components, versions] = await Promise.all([
        fetchCmdbCards('KomponenteType'), fetchCmdbCards('KomponenteVersion'),
      ]);
      if (seq !== loadSeq.current) return;
      setData({ components: components.rows, versions: versions.rows });
      setError(null);
    } catch (caught) {
      if (seq === loadSeq.current) setError(errorMessage(caught));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { ++loadSeq.current; ++dialogSeq.current; };
  }, [load]);

  const groups = useMemo(() => groupComponents(data?.components ?? [], data?.versions ?? []), [data]);
  const visibleGroups = useMemo(() => filterComponentGroups(groups, search, filter), [groups, search, filter]);
  const visibleSummary = useMemo(() => ({
    versions: visibleGroups.reduce((sum, group) => sum + group.versions.length, 0),
    manufacturers: new Set(visibleGroups.map((group) => group.manufacturer.trim().toLocaleLowerCase('de')).filter(Boolean)).size,
  }), [visibleGroups]);
  const focusId = Number(params.get('id')) || null;
  const focusClass = params.get('klasse');
  const focusGroup = focusId === null ? undefined : groups.find((group) =>
    focusClass === 'KomponenteVersion'
      ? group.versions.some((version) => version.id === focusId)
      : group.component?.id === focusId);
  const focusKey = focusGroup?.key;
  const focusToken = focusId === null ? null : `${focusClass}:${focusId}`;

  useEffect(() => {
    if (!focusKey || !focusToken) { focusedRef.current = null; return; }
    if (focusedRef.current === focusToken) return;
    focusedRef.current = focusToken;
    setSearch('');
    setFilter('all');
    setExpanded((current) => new Set([...current, focusKey]));
    // Allow the table's pagination reveal and expanded content to commit first.
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const target = containerRef.current?.querySelector('.component-version.is-focused')
          ?? containerRef.current?.querySelector('.stamm-focus');
        target?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusKey, focusToken]);

  const clearFocus = (): void => {
    focusedRef.current = null;
    if (!params.has('id')) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const openForm = async (targetMeta: CmdbClassMeta, row: CmdbCardRow | null, group?: ComponentGroup): Promise<void> => {
    if (!canWrite) return;
    const seq = ++dialogSeq.current;
    setDialogBusy(true);
    try {
      const targets = referenceTargets(targetMeta);
      const latest = group ? latestComponentVersion(group) : null;
      const [options, fresh, template] = await Promise.all([
        targets.length ? fetchCmdbOptions(targets) : Promise.resolve({ options: {} }),
        row ? fetchCmdbCard(targetMeta.name, row.id) : Promise.resolve(null),
        // A template deleted meanwhile still serves as defaults from the loaded list.
        latest ? fetchCmdbCard(targetMeta.name, latest.id).catch(() => latest) : Promise.resolve(null),
      ]);
      if (seq !== dialogSeq.current) return;
      setDialog({ kind: 'form', meta: targetMeta, row: fresh, options: options.options,
        initialValues: group?.component ? newVersionValues(targetMeta.attributes, group.component, template) : undefined,
        notice: template ? `Angaben aus Version ${componentVersionLabel(template)} übernommen. `
          + 'Firmware-/Softwareversion und CPE Update bitte neu erfassen.' : undefined,
      });
    } catch (caught) {
      if (seq === dialogSeq.current) toast.error(errorMessage(caught));
    } finally {
      if (seq === dialogSeq.current) setDialogBusy(false);
    }
  };

  const openDelete = async (targetMeta: CmdbClassMeta, row: CmdbCardRow): Promise<void> => {
    if (!canWrite) return;
    const seq = ++dialogSeq.current;
    setDialogBusy(true);
    try {
      const fresh = await fetchCmdbCard(targetMeta.name, row.id);
      if (seq === dialogSeq.current) setDialog({ kind: 'delete', meta: targetMeta, row: fresh });
    } catch (caught) {
      if (seq === dialogSeq.current) toast.error(errorMessage(caught));
    } finally {
      if (seq === dialogSeq.current) setDialogBusy(false);
    }
  };

  const afterWrite = async (message: string): Promise<void> => {
    setDialog({ kind: 'closed' });
    toast.success(message);
    await load();
    await onCountsChange();
  };

  const actions = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => (
    <div className="stamm-actions">
      <button type="button" className="stamm-action-button" disabled={!canWrite || dialogBusy}
        aria-label={`${row.label} bearbeiten`} title={canWrite ? 'Bearbeiten' : readOnlyNotice('cmdb.write')}
        onClick={() => void openForm(targetMeta, row)}><Icon name="edit" size={18} /></button>
      <button type="button" className="stamm-action-button stamm-delete-button" disabled={!canWrite || dialogBusy}
        aria-label={`${row.label} löschen`} title={canWrite ? 'Löschen' : readOnlyNotice('cmdb.write')}
        onClick={() => void openDelete(targetMeta, row)}><Icon name="trash" size={18} /></button>
    </div>
  );

  const addVersion = (group: ComponentGroup): ReactNode => group.component && versionMeta.can_create ? (
    <button type="button" className="component-add-version" disabled={!canWrite || dialogBusy}
      title={canWrite ? `Version für ${group.name} anlegen` : readOnlyNotice('cmdb.write')}
      aria-label={`Version für ${group.name} anlegen`}
      onClick={(event) => { event.stopPropagation(); void openForm(versionMeta, null, group); }}>
      <Icon name="plus" />Version
    </button>
  ) : null;

  const detailFields = (targetMeta: CmdbClassMeta, row: CmdbCardRow): ReactNode => (
    <dl className="stamm-detail-grid">
      {targetMeta.attributes.map((attr) => (
        <div key={attr.name}><dt title={attr.help}>{attr.label}{attr.system ? <Chip>System</Chip> : null}</dt><dd>{renderValue(attr, row)}</dd></div>
      ))}
    </dl>
  );

  const columns: readonly DataTableColumn<ComponentGroup>[] = [
    { key: 'component', header: 'Komponente', width: '38%', sortValue: (group) => group.name,
      render: (group) => <div className="component-identity">
        <ManufacturerLogo manufacturer={group.manufacturer} className="component-logo" />
        <div className="component-name"><strong>{group.name}</strong><span>{group.manufacturer || 'Hersteller nicht angegeben'}</span>
          {!group.component ? <span className="component-unassigned">Ohne Komponentenzuordnung</span> : null}
        </div>
      </div>,
    },
    { key: 'kind', header: 'Typ', width: '13%', sortValue: componentKindLabel,
      render: (group) => <Chip>{componentKindLabel(group)}</Chip>,
    },
    { key: 'versions', header: 'Versionen', sortValue: (group) => group.versions.length,
      render: (group) => <div className="component-versions-preview">
        {group.versions.slice(0, 3).map((version) => <button key={version.id} type="button" className="component-version-link"
          aria-label={`Version ${componentVersionLabel(version)} von ${group.name} öffnen`}
          onClick={(event) => { event.stopPropagation(); setParams({ klasse: 'KomponenteVersion', id: String(version.id) }); }}>
          <Chip mono>{componentVersionLabel(version)}</Chip>
        </button>)}
        {group.versions.length > 3 ? <span className="component-more">+{group.versions.length - 3} weitere</span> : null}
        {group.versions.length === 0 ? <span className="component-no-versions">Noch keine Version</span> : null}
      </div>,
    },
    { key: 'actions', header: <span className="visually-hidden">Aktionen</span>, mobileLabel: 'Aktionen', width: '128px', align: 'right', render: addVersion },
  ];

  const renderDetail = (group: ComponentGroup): ReactNode => (
    <div className="component-expanded">
      <div className="component-expanded-head">
        <strong>{countLabel(group.versions.length, 'Version', 'Versionen')}</strong>
        {group.component ? <div className="component-detail-actions">{actions(componentMeta, group.component)}</div> : null}
      </div>
      {group.versions.length ? <div className="component-version-list">
        {group.versions.map((version) => {
          const focused = focusClass === 'KomponenteVersion' && focusId === version.id;
          return <article key={version.id} className={`component-version${focused ? ' is-focused' : ''}`} aria-label={`${group.name} ${componentVersionLabel(version)}`}>
            <div className="component-version-head">
              <div className="component-version-title"><StammIcon name="layers" /><strong>{componentVersionLabel(version)}</strong></div>
              {actions(versionMeta, version)}
            </div>
            <details className="component-technical" open={focused || undefined}>
              <summary>Technische Details<span className="visually-hidden"> zu Version {componentVersionLabel(version)}</span></summary>
              {detailFields(versionMeta, version)}
            </details>
          </article>;
        })}
      </div> : <EmptyState compact title="Noch keine Version erfasst" action={addVersion(group)}>Ergänzen Sie die erste Software- oder Firmwareversion.</EmptyState>}
      {group.component ? <details className="component-technical component-master-data"><summary>Komponenten-Details</summary>{detailFields(componentMeta, group.component)}</details> : null}
    </div>
  );

  return (
    <section ref={containerRef} className="card stamm-content component-catalog" aria-labelledby="component-catalog-title" aria-busy={loading}>
      <div className="card-head table-head stamm-content-head">
        <div className="stamm-title-row"><h2 id="component-catalog-title">Komponenten</h2></div>
        {componentMeta.can_create ? <button type="button" className="primary stamm-create" disabled={!canWrite || dialogBusy}
          title={canWrite ? undefined : readOnlyNotice('cmdb.write')} onClick={() => void openForm(componentMeta, null)}><Icon name="plus" />Komponente anlegen</button> : null}
      </div>
      <div className="table-toolbar">
        <input type="search" value={search}
          placeholder="Komponente, Hersteller oder Version suchen…" aria-label="Komponenten und Versionen durchsuchen"
          onChange={(event) => { clearFocus(); setSearch(event.target.value); }} />
        <label className="component-filter-label"><span className="visually-hidden">Nach Versionen filtern</span><select className="component-filter" value={filter}
          onChange={(event) => { clearFocus(); setFilter(event.target.value as ComponentGroupFilter); }}>
          <option value="all">Alle Komponenten</option><option value="with-versions">Mit Versionen</option><option value="without-versions">Ohne Versionen</option>
        </select></label>
        {data ? <div className="table-toolbar-options">
          <span className="table-count" aria-live="polite">
            {countLabel(visibleGroups.length, 'Komponente', 'Komponenten')}
            {' · '}{countLabel(visibleSummary.versions, 'Version', 'Versionen')}
            {' · '}{visibleSummary.manufacturers} Hersteller
          </span>
        </div> : null}
      </div>
      {error ? <Banner kind="error" sub={data ? 'Die angezeigten Daten konnten nicht aktualisiert werden.' : undefined}>{error}</Banner> : null}
      {loading && !data ? <PageSpinner text="Lädt Komponenten und Versionen…" /> : data && visibleGroups.length === 0 ? (
        <EmptyState title={search || filter !== 'all' ? 'Keine passenden Komponenten' : 'Noch keine Komponenten'}
          action={search || filter !== 'all' ? <button type="button" onClick={() => { setSearch(''); setFilter('all'); }}>Filter zurücksetzen</button>
            : canWrite && componentMeta.can_create ? <button type="button" disabled={dialogBusy} onClick={() => void openForm(componentMeta, null)}><Icon name="plus" />Komponente anlegen</button> : undefined}>
          {search || filter !== 'all' ? 'Passen Sie die Suche oder den Versionsfilter an.' : 'Legen Sie eine Komponente an und ergänzen Sie anschließend ihre Versionen.'}
        </EmptyState>
      ) : data ? <div className="component-table"><DataTable rows={visibleGroups} columns={columns} rowKey={(group) => group.key}
        renderDetail={renderDetail} expandedKeys={expanded} onToggleRow={(key) => {
          clearFocus(); setExpanded((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
        }} pageSize={10} paginationResetKey={`${search}:${filter}`} revealRowKey={focusKey} mobileBreakpoint={760}
        caption="Komponenten und ihre Versionen" rowClassName={(group) => group.key === focusKey ? 'stamm-focus' : undefined} /></div> : null}

      {dialog.kind === 'form' ? <CardFormModal meta={dialog.meta} row={dialog.row} initialValues={dialog.initialValues} notice={dialog.notice}
        primaryFields={dialog.meta.name === 'KomponenteVersion' ? ['KomponenteTypeID', 'Version'] : undefined}
        readOnlyFields={dialog.initialValues ? ['KomponenteTypeID'] : undefined}
        options={dialog.options} lookups={meta.lookups} onClose={() => setDialog({ kind: 'closed' })}
        onSubmit={async (values) => {
          const seq = dialogSeq.current;
          const saved = dialog.row ? await updateCmdbCard(dialog.meta.name, dialog.row.id, values) : await createCmdbCard(dialog.meta.name, values);
          if (seq !== dialogSeq.current) return;
          await afterWrite(`${dialog.meta.label} „${saved.label}“ ${dialog.row ? 'gespeichert' : 'angelegt'}.`);
          if (seq !== dialogSeq.current) return;
          setSearch(''); setFilter('all'); focusedRef.current = null;
          setParams({ klasse: dialog.meta.name, id: String(saved.id) });
        }} /> : null}
      {dialog.kind === 'delete' ? <DeleteCardModal meta={dialog.meta} row={dialog.row} blockers={dialog.row.referenced_by ?? []}
        onClose={() => setDialog({ kind: 'closed' })} onConfirm={async (cascade) => {
          const seq = dialogSeq.current;
          await deleteCmdbCard(dialog.meta.name, dialog.row.id, cascade);
          if (seq !== dialogSeq.current) return;
          clearFocus(); await afterWrite(`${dialog.meta.label} „${dialog.row.label}“ gelöscht.`);
        }} /> : null}
    </section>
  );
}
